import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Coffee, Sparkles, ArrowRight } from "lucide-react";

/**
 * Phase 25 — Public "Join Rewards" tease for the storefront.
 *
 * Fetches the public-safe loyalty program summary (GET /api/loyalty/program)
 * and renders a glossy invite strip on Home + Menu. Self-hides if the program
 * is inactive or the tenant hasn't configured one.
 *
 * - On Home: full-width band with hero CTA → /signup
 * - On Menu / other pages: compact rail variant via `compact` prop
 */
export default function LoyaltyTeaseStrip({ compact = false }) {
  const [program, setProgram] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/loyalty/program");
        if (!cancelled && data?.is_active) setProgram(data);
      } catch (_) { /* silent — tenant may not have it on */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!program) return null;

  const rate         = program.points_per_dollar || 1;
  const signupBonus  = program.points_signup_bonus || 0;
  const redeemRate   = program.points_redemption_rate || 100;
  const dollarsPerHundred = (100 / redeemRate).toFixed(2);
  const punchCard    = (program.punch_cards || []).find((c) => c.is_active);

  if (compact) {
    return (
      <Link
        to="/signup"
        data-testid="loyalty-tease-compact"
        className="block rounded-2xl overflow-hidden mb-6 bg-gradient-to-r from-[#1A1410] via-[#2A1C18] to-[#3A2A24] text-white px-5 py-4 hover:-translate-y-0.5 transition-transform"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-amber-300/15 border border-amber-300/40 flex items-center justify-center shrink-0">
              <Trophy className="w-4 h-4 text-amber-300" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                Join {program.name || "Rewards"} — {signupBonus > 0 ? `${signupBonus} bonus points on sign-up` : `earn ${rate} pt / $1`}
              </div>
              {punchCard ? (
                <div className="text-[11px] text-white/65 truncate">
                  Plus {punchCard.name}: buy {punchCard.items_required} → {punchCard.reward_label}
                </div>
              ) : (
                <div className="text-[11px] text-white/65 truncate">
                  Redeem {redeemRate} pts for ${dollarsPerHundred} off · free to join
                </div>
              )}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-300 shrink-0" />
        </div>
      </Link>
    );
  }

  return (
    <section
      data-testid="loyalty-tease-strip"
      className="relative overflow-hidden bg-gradient-to-br from-[#1A1410] via-[#2A1C18] to-[#3A2A24] text-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-300/15 border border-amber-300/40 text-amber-200 text-[10px] tracking-[0.28em] uppercase font-medium">
            <Trophy className="w-3 h-3" /> {program.name || "Rewards"} · free to join
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-4 max-w-xl">
            {signupBonus > 0 ? (
              <>Sign up, take <em className="font-normal italic text-amber-200">{signupBonus} points</em> on us.</>
            ) : (
              <>Every dollar you spend, <em className="font-normal italic text-amber-200">we say thanks</em>.</>
            )}
          </h2>
          <p className="mt-4 text-white/75 max-w-lg leading-relaxed">
            Earn <b>{rate} point per $1 spent</b>, climb tiers for bigger
            multipliers, and redeem <b>{redeemRate} points for ${dollarsPerHundred} off</b> on any
            future order. {punchCard && <>Plus our <b>{punchCard.name}</b> punch card — buy {punchCard.items_required}, get {punchCard.reward_label}.</>}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/signup"
              data-testid="loyalty-tease-join"
              className="inline-flex items-center gap-2 bg-amber-300 text-stone-900 font-medium text-sm px-5 py-3 rounded-full hover:bg-amber-200 transition"
            >
              <Sparkles className="w-4 h-4" /> Join rewards
            </Link>
            <Link
              to="/account"
              data-testid="loyalty-tease-existing"
              onClick={() => {
                try {
                  import("@/components/AccountHeader").then((m) =>
                    m.rememberAccountReturn({
                      label: "Loyalty",
                      href:  typeof window !== "undefined"
                              ? window.location.pathname + window.location.search
                              : "/",
                    }));
                } catch (_e) { /* no-op */ }
              }}
              className="inline-flex items-center gap-2 border border-white/25 text-white/85 text-sm px-5 py-3 rounded-full hover:bg-white/10 transition"
            >
              Already a member? Sign in
            </Link>
          </div>
        </div>

        <ul className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(program.tiers || []).slice(0, 4).map((t) => (
            <li
              key={t.name}
              className="rounded-2xl bg-white/5 border border-white/10 p-4"
              data-testid={`loyalty-tease-tier-${t.name.toLowerCase()}`}
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium tracking-[0.18em] uppercase">{t.name}</span>
                <span className="text-amber-200">x{t.multiplier}</span>
              </div>
              <div className="text-[11px] text-white/55 mt-1">
                Reach at {Number(t.min_points_year || 0).toLocaleString()}+ pts/year
              </div>
              {t.perk && (
                <div className="text-[12px] text-white/80 mt-2 leading-snug italic">{t.perk}</div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Decorative coffee-cup pattern */}
      <Coffee className="absolute -right-6 -bottom-6 w-44 h-44 text-amber-300/5 pointer-events-none" />
    </section>
  );
}
